import { useState, useCallback } from 'react';
import { buildApiUrl, apiFetch, buildResponseError, parseResponseBody } from '../utils/http';

const clearAuth = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent('auth-expired'));
};

const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const makeRequest = useCallback(async (endpoint, options = {}) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    console.log('[API] Request initiated', {
      requestId,
      timestamp: new Date().toISOString(),
      endpoint,
      options
    });

    setLoading(true);
    setError(null);

    try {
      const {
        method = 'GET',
        data = null,
        body,
        headers = {},
        isFormData = false
      } = options;

      const url = buildApiUrl(endpoint);
      console.log('[API] Request URL built', {
        requestId,
        endpoint,
        fullUrl: url,
        nodeEnv: process.env.NODE_ENV,
        apiUrlFromEnv: process.env.REACT_APP_API_URL
      });

      const startTime = performance.now();
      const response = await apiFetch(endpoint, {
        method,
        headers,
        data,
        body,
        isFormData
      });
      const endTime = performance.now();

      console.log('[API] Response received', {
        requestId,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        duration: `${(endTime - startTime).toFixed(2)}ms`,
        headers: Object.fromEntries([...response.headers.entries()]),
        timestamp: new Date().toISOString()
      });

      const responseData = await parseResponseBody(response);

      if (!response.ok) {
        const isLoginAttempt =
          endpoint.includes('/auth/login')
          || endpoint.includes('/auth/register')
          || endpoint.includes('/auth/google');

        if (response.status === 401 && !isLoginAttempt) {
          console.error('[API] Authentication error - expiring client session', {
            requestId,
            endpoint
          });
          clearAuth();
        }

        const error = buildResponseError(
          response,
          responseData,
          response.status === 401 && isLoginAttempt
            ? 'Invalid credentials'
            : `Request failed: ${response.status}`
        );

        console.error('[API] Request failed', {
          requestId,
          status: response.status,
          errorMessage: error.message,
          errorData: responseData
        });

        throw error;
      }

      const normalizedResponse =
        responseData && typeof responseData === 'object'
          ? responseData
          : { data: responseData };

      console.log('[API] Response parsed successfully', {
        requestId,
        responseKeys: Object.keys(normalizedResponse),
        timestamp: new Date().toISOString()
      });

      return normalizedResponse;
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      console.error('[API] Request error', {
        requestId,
        endpoint,
        errorMessage,
        errorName: err.name,
        errorStack: err.stack,
        timestamp: new Date().toISOString()
      });
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      console.log('[API] Request completed', {
        requestId,
        endpoint,
        timestamp: new Date().toISOString()
      });
    }
  }, []);

  const get = useCallback((endpoint, options = {}) => (
    makeRequest(endpoint, { ...options, method: 'GET' })
  ), [makeRequest]);

  const post = useCallback((endpoint, data, options = {}) => (
    makeRequest(endpoint, { ...options, method: 'POST', data })
  ), [makeRequest]);

  const put = useCallback((endpoint, data, options = {}) => (
    makeRequest(endpoint, { ...options, method: 'PUT', data })
  ), [makeRequest]);

  const del = useCallback((endpoint, options = {}) => (
    makeRequest(endpoint, { ...options, method: 'DELETE' })
  ), [makeRequest]);

  const patch = useCallback((endpoint, data, options = {}) => (
    makeRequest(endpoint, { ...options, method: 'PATCH', data })
  ), [makeRequest]);

  const upload = useCallback((endpoint, formData, options = {}) => (
    makeRequest(endpoint, {
      ...options,
      method: options.method || 'PUT',
      data: formData,
      isFormData: true
    })
  ), [makeRequest]);

  return {
    loading,
    error,
    clearError: () => setError(null),
    makeRequest,
    get,
    post,
    put,
    delete: del,
    patch,
    upload,
    clearAuth
  };
};

export default useApi;
